import {
  EventStore,
  ResourceRequestOptions,
  SingleResponse,
} from '@soniq/public-resource'
import {
  ContactRelationshipWithId,
  ContactWithId,
  validateContact,
} from '@soniq/schema-connection'
import { ensureCreatedFields } from '@soniq/schema'
import {
  contactEventsBuilder,
  contactRelationshipEventsBuilder,
} from '../domainEvents'
import { generateSubjectRelationships } from './generateContactSubjectAndRelationships'
import { NewContactOrExistingRelationship } from './types'
import { uuidv7 } from '@pogokid/util'

/**
 * Create a new contact that is a student,
 * including the associated user contact record
 * and any linked contacts
 */
export async function createStudentWithContacts(
  options: ResourceRequestOptions,
  profileId: string,
  userId: string,
  student: ContactWithId,
  relationships?: ContactRelationshipWithId[],
  contacts?: ContactWithId[]
): Promise<SingleResponse<ContactWithId>> {
  if (!options.db) {
    throw new Error(
      'Cannot create student with contacts without db connection'
    )
  }
  const eventStore = EventStore.new(options.db)
  const { isValid, cleanedValues } = validateContact({
    ...student,
    is_student: true,
    meta: {},
  })
  if (isValid) {
    const resolvedId = student.id ?? uuidv7()
    const newStudent: ContactWithId = {
      ...cleanedValues,
      id: resolvedId,
    }
    const relationshipToStudent =
      ensureCreatedFields<ContactRelationshipWithId>(userId, {
        id: uuidv7(),
        subject_id: userId,
        contact_id: resolvedId,
        relationship: 'student',
        profile_id: profileId,
        deleted_at: null,
        deleted_by: null,
      })
    // Everything created here is brand new (the student, any linked
    // contacts and their relationships all have freshly generated ids), so
    // emit per-entity `created` events rather than the deprecated
    // `upserted` batch event.
    //
    // Ordering matters: the linked contacts are created before the student
    // because the student's `billing_contact_id` is a foreign key into the
    // contact table and may point at one of these new contacts. Contacts
    // (including the student) are in turn created before relationships, so
    // every contact referenced by a relationship already exists.
    const contactEvents = [
      ...(contacts ?? []),
      newStudent,
    ].map((contact) =>
      contactEventsBuilder.created({
        profile_id: profileId,
        user_id: userId,
        payload: contact,
      })
    )
    const relationshipEvents = [
      relationshipToStudent,
      ...(relationships ?? []).map(
        (relationship): ContactRelationshipWithId => ({
          ...relationship,
          // Keep the subject the caller chose. A linked contact gets two
          // relationships: one to the student (holding the chosen
          // relationship, e.g. parent) and one to the teacher, which is
          // what puts the contact in the teacher's own contact list.
          // Overwriting the subject here would point both at the student
          // and show the contact twice on the student.
          subject_id: relationship.subject_id || resolvedId,
        })
      ),
    ].map((relationship) =>
      contactRelationshipEventsBuilder.created({
        profile_id: profileId,
        user_id: userId,
        payload: relationship,
      })
    )
    await eventStore.appendBatch([
      ...contactEvents,
      ...relationshipEvents,
    ])
    return { data: newStudent }
  } else {
    throw new Error('Cannot create student, invalid data')
  }
}

export async function upsertContactsAndRelationships(
  options: ResourceRequestOptions,
  profileId: string,
  contacts?: ContactWithId[],
  relationships?: ContactRelationshipWithId[]
): Promise<SingleResponse<ContactRelationshipWithId[]>> {
  if (!options.db) {
    throw new Error(
      'Cannot upsert contacts and relationships without db connection'
    )
  }
  const eventStore = EventStore.new(options.db)
  const events = await Promise.all([
    ...(contacts ?? []).map((contact) =>
      contactEventsBuilder.updatedOrCreated(
        options,
        profileId,
        contact.created_by,
        contact
      )
    ),
    ...(relationships ?? []).map((relationship) =>
      contactRelationshipEventsBuilder.updatedOrCreated(
        options,
        profileId,
        relationship.created_by,
        relationship
      )
    ),
  ])
  await eventStore.appendBatch(events)
  return { data: relationships ?? [] }
}

export async function syncContactsAndRelationships(
  options: ResourceRequestOptions,
  profileId: string,
  userId: string,
  subject: ContactWithId,
  existingRelationships: ContactRelationshipWithId[],
  updatedRelationships: NewContactOrExistingRelationship[]
): Promise<{ billingContactId: string | null }> {
  if (!options.db) {
    throw new Error(
      'Cannot sync contacts and relationships without db connection'
    )
  }
  const eventStore = EventStore.new(options.db)
  const subjectId = subject.id

  const removedIds = existingRelationships
    .filter(
      ({ id }) =>
        !updatedRelationships.some(
          (updated) => 'id' in updated && id === updated.id
        )
    )
    .map(({ id }) => id)

  const deleteEvents = removedIds.map((id) =>
    contactRelationshipEventsBuilder.deleted({
      profile_id: profileId,
      user_id: userId,
      payload: { id },
    })
  )

  const upsertEvents: Awaited<
    ReturnType<typeof contactEventsBuilder.updatedOrCreated>
  >[] = []
  let billingContactId: string | null = null
  if (updatedRelationships.length) {
    const {
      newContacts,
      newContactRelationships,
      billingContactId: resolvedBillingContactId,
    } = generateSubjectRelationships(
      userId,
      subjectId,
      updatedRelationships
    )
    billingContactId = resolvedBillingContactId
    const events = await Promise.all([
      ...newContacts.map((contact) =>
        contactEventsBuilder.updatedOrCreated(
          options,
          profileId,
          userId,
          contact
        )
      ),
      ...newContactRelationships.map((relationship) =>
        contactRelationshipEventsBuilder.updatedOrCreated(
          options,
          profileId,
          userId,
          relationship
        )
      ),
    ])
    upsertEvents.push(...events)
  }

  // When the billing contact changed, update the subject in the SAME batch
  // so it shares a correlation id with the contact events and is ordered
  // after them. This guarantees billing_contact_id never references a
  // contact whose `created` event hasn't been appended yet.
  const billingEvents =
    billingContactId !== (subject.billing_contact_id ?? null)
      ? [
          contactEventsBuilder.updated({
            profile_id: profileId,
            user_id: userId,
            payload: { ...subject, billing_contact_id: billingContactId },
          }),
        ]
      : []

  const allEvents = [
    ...deleteEvents,
    ...upsertEvents,
    ...billingEvents,
  ]
  if (allEvents.length) {
    await eventStore.appendBatch(allEvents)
  }

  return { billingContactId }
}
